import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { apiUrl } from '../config/api';
import { AlertTriangle } from 'lucide-react';
import { formatDateDMY } from '../utils/dateUtils';
import { toCurrency } from '../utils/adminMetrics';

// A loan application the client saved to finish later (/api/loan-drafts/mine),
// shown on their dashboard. Continue opens Loan Application straight on the
// form (?resume=1); discard deletes it. Renders nothing when there is none.
// saved: the summary's `saved_application` (GET /api/loans/my-summary); when
// it is not given, the card loads the saved application itself.
// onDiscarded: called after it was discarded (to reload the summary).
const SavedApplicationCard = ({ className = 'mb-4', onContinue, saved, onDiscarded }) => {
  const fromSummary = saved !== undefined;
  const [draft, setDraft] = useState(fromSummary ? saved : null);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (fromSummary) {
      setDraft(saved);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(apiUrl('/api/loan-drafts/mine'), { headers: { Authorization: `Bearer ${token}` } });
        if (!cancelled && res.data?.credit_assessment_id) setDraft(res.data);
      } catch {
        // None saved (404), or not available — nothing to show.
      }
    })();
    return () => { cancelled = true; };
  }, [fromSummary, saved]);

  if (!draft) return null;

  const discard = async () => {
    setBusy(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(apiUrl('/api/loan-drafts/mine'), { headers: { Authorization: `Bearer ${token}` } });
    } catch { /* already gone */ }
    setDraft(null);
    setBusy(false);
    if (typeof onDiscarded === 'function') onDiscarded();
  };

  const months = Number(draft.term_months) || null;

  return (
    <div role="status" className={`${className} rounded-xl border border-amber-300 dark:border-amber-700/60 bg-amber-50 dark:bg-amber-900/20 p-4 flex flex-wrap items-center justify-between gap-3`}>
      <div className="flex items-start gap-3 min-w-0">
        <div className="w-9 h-9 rounded-lg bg-amber-100 dark:bg-amber-900/40 text-amber-600 dark:text-amber-400 flex items-center justify-center flex-shrink-0">
          <AlertTriangle size={18} />
        </div>
        <div className="min-w-0">
          <p className="text-sm font-semibold text-amber-900 dark:text-amber-200">Saved loan application — not submitted yet</p>
          <p className="text-xs text-amber-800 dark:text-amber-300 mt-0.5">
            {draft.message || 'Finish it to send it to us; you will only need to take your photo again.'}
          </p>
          <p className="text-xs font-semibold text-amber-900 dark:text-amber-200 mt-1">
            {draft.principal_amount ? <>{toCurrency(draft.principal_amount)}</> : 'Your application'}
            {months ? <> over {months} month{months === 1 ? '' : 's'}</> : null}
            {draft.saved_at ? <span className="font-normal"> · saved {formatDateDMY(draft.saved_at)}</span> : null}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={discard}
          disabled={busy}
          className="px-3 py-1.5 rounded-lg text-xs font-semibold text-amber-800 dark:text-amber-300 hover:bg-amber-100 dark:hover:bg-slate-700 disabled:opacity-50"
        >
          Discard
        </button>
        <button
          type="button"
          onClick={() => (typeof onContinue === 'function' ? onContinue() : navigate('/dashboard?apply=resume'))}
          disabled={busy}
          className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50"
        >
          Continue application
        </button>
      </div>
    </div>
  );
};

export default SavedApplicationCard;
